import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
    faDumbbell,
    faRotate,
    faPenNib,
    faBookOpen,
    faMicrophone,
    faBrain,
    faQuestion,
    faPrint,
    faPuzzlePiece,
    faEye,
    faList,
    faLanguage,
    faSpellCheck,
    faWandMagicSparkles,
    faMoneyBillTransfer,
    faChartLine,
    faGraduationCap,
    faBookmark,
    faNewspaper,
} from "@fortawesome/free-solid-svg-icons";

export type NavLinkItem = {
    to: string;
    icon: IconDefinition | null;
    labelKey: string;
};

export type NavGroup = {
    headingKey: string;
    links: NavLinkItem[];
};

// The header's "More" dropdown and mobile overflow panel, and now the
// footer too, all read from this single catalog instead of each maintaining
// its own link list - that's how the flat footer list ended up missing 14
// real pages in the first place. Group headings deliberately reuse the
// About page's own feature-category names (see aboutPage.json's
// `features.groups`) so every surface describes the same catalog the same
// way instead of drifting apart again. "Practice" isn't one of About's four
// categories, but it is now a real route (see PracticeNavLink in
// HeaderComponent.tsx), so it gets its own group here too.
export const navGroups = {
    groupPractice: {
        headingKey: "header.nav.groupPractice",
        links: [
            { to: "/practice", icon: faDumbbell, labelKey: "header.nav.practiceHub" },
            { to: "/conjugation", icon: faRotate, labelKey: "header.nav.conjugation" },
            { to: "/adjective-conjugation", icon: faRotate, labelKey: "header.nav.adjectiveConjugation" },
            { to: "/kana-writing-practice", icon: faPenNib, labelKey: "header.nav.kanaWritingPractice" },
            { to: "/kanji-writing-practice", icon: faPenNib, labelKey: "header.nav.kanjiWritingPractice" },
            { to: "/reading-practice", icon: faBookOpen, labelKey: "header.nav.readingPractice" },
            { to: "/speaking-practice", icon: faMicrophone, labelKey: "header.nav.speakingPractice" },
            { to: "/kanji-roulette", icon: faRotate, labelKey: "header.nav.kanjiRoulette" },
            { to: "/kanji-memory-quiz", icon: faBrain, labelKey: "header.nav.kanjiMemoryQuiz" },
            { to: "/kanji-quiz", icon: faQuestion, labelKey: "header.nav.kanjiQuiz" },
            { to: "/worksheets", icon: faPrint, labelKey: "header.nav.worksheets" },
        ],
    },
    groupKanjiKana: {
        headingKey: "header.nav.groupKanjiKana",
        links: [
            { to: "/radicals", icon: faPuzzlePiece, labelKey: "header.nav.radicals" },
            { to: "/similar-kanji", icon: faEye, labelKey: "header.nav.similarKanji" },
            { to: "/blog/full-kanji-list", icon: faList, labelKey: "header.nav.fullKanjiList" },
            { to: "/numbers", icon: faLanguage, labelKey: "header.nav.numbers" },
        ],
    },
    groupTools: {
        headingKey: "header.nav.groupTools",
        links: [
            { to: "/grammar", icon: faSpellCheck, labelKey: "header.nav.grammar" },
            { to: "/converter", icon: faWandMagicSparkles, labelKey: "header.nav.converter" },
            { to: "/romaji-converter", icon: faLanguage, labelKey: "header.nav.romaji" },
            { to: "/currency-converter", icon: faMoneyBillTransfer, labelKey: "header.nav.currencyConverter" },
            { to: "/wanikani", icon: faChartLine, labelKey: "header.nav.wanikani" },
        ],
    },
    groupResources: {
        headingKey: "header.nav.groupResources",
        links: [
            { to: "/learning-roadmap", icon: faGraduationCap, labelKey: "header.nav.learningRoadmap" },
            { to: "/saved-words", icon: faBookmark, labelKey: "header.nav.savedWords" },
            { to: "/study-materials", icon: faBookOpen, labelKey: "header.nav.studyMaterials" },
            { to: "/jlpt-test-sites", icon: faGraduationCap, labelKey: "header.nav.jlptTestSites" },
            { to: "/blog", icon: faNewspaper, labelKey: "header.nav.blog" },
            { to: "/about", icon: null, labelKey: "header.nav.about" },
        ],
    },
} satisfies Record<string, NavGroup>;

// The header's "More" dropdown iterates this flat array of the four groups,
// in the same order they're defined above.
export const moreMenuGroups: NavGroup[] = Object.values(navGroups);
